import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock } from "@phosphor-icons/react";

// Auto-logout after 1 h of no user activity. 30 s warning countdown modal.
// Fires for ALL users including super admin. Activity = pointerdown / keydown /
// scroll / touchstart on the document.
const IDLE_MS = 60 * 60 * 1000;   // 1 hour
const WARN_MS = 30 * 1000;         // 30 s countdown

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const [warnOpen, setWarnOpen] = useState(false);
  const [remaining, setRemaining] = useState(WARN_MS / 1000);
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);
  const tickTimer = useRef(null);

  const clearAll = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    idleTimer.current = warnTimer.current = tickTimer.current = null;
  }, []);

  const forceLogout = useCallback(() => {
    clearAll();
    setWarnOpen(false);
    try { logout && logout(); } catch { /* ignore */ }
    try { window.location.href = "/login"; } catch { /* ignore */ }
  }, [clearAll, logout]);

  const showWarning = useCallback(() => {
    setRemaining(WARN_MS / 1000);
    setWarnOpen(true);
    tickTimer.current = setInterval(() => {
      setRemaining((r) => (r > 1 ? r - 1 : 0));
    }, 1000);
    warnTimer.current = setTimeout(() => forceLogout(), WARN_MS);
  }, [forceLogout]);

  const scheduleIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => showWarning(), IDLE_MS);
  }, [showWarning]);

  const resetIdle = useCallback(() => {
    // Ignore resets while the warning modal is open — user must actively click.
    if (warnOpen) return;
    scheduleIdle();
  }, [warnOpen, scheduleIdle]);

  useEffect(() => {
    if (!user) return;              // no timer when signed out
    scheduleIdle();
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    for (const e of events) window.addEventListener(e, resetIdle, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, resetIdle);
      clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, warnOpen]);

  const stay = () => {
    clearAll();
    setWarnOpen(false);
    scheduleIdle();
  };

  if (!user) return null;
  return (
    <Dialog open={warnOpen} onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="sm:max-w-md" data-testid="idle-warning-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={20} className="text-amber-500" /> Are you still there?
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 text-sm text-slate-700">
          You've been idle for a while. You'll be signed out in{" "}
          <span className="font-bold text-rose-600 tabular-nums text-lg" data-testid="idle-countdown">{remaining}</span>{" "}
          second{remaining === 1 ? "" : "s"}.
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={forceLogout} data-testid="idle-signout">Sign out</Button>
          <Button onClick={stay} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="idle-stay">Stay signed in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
